import {
  AllCommunityModule,
  ColDef,
  ColumnState,
  GetRowIdParams,
  GridReadyEvent,
  IsFullWidthRowParams,
  ModuleRegistry,
  PostSortRowsParams,
  RowClickedEvent,
  RowHeightParams
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CompactContractsList from '@/components/CompactContractsList';
import PageHeader from '@/components/PageHeader';
import { CONTRACT_STATUSES, CONTRACTS_PATH } from '@/constants/contracts';
import { DRUG_CATEGORIES } from '@/constants/programs';
import AppContext from '@/contexts/appContext';
import ContractsGridContext, {
  type ContractsGridContextInterface
} from '@/contexts/contractsGridContext';
import { useDugoutContracts } from '@/hooks/useDugoutContracts';
import useDugoutActiveProgramFamilies from '@/hooks/useDugoutProgramFamilies';
import useIsSmallScreen from '@/hooks/useIsSmallScreen';
import useJurisdictions from '@/hooks/useJurisdictions';
import usePhaseTypes from '@/hooks/usePhaseTypes';
import type { ContractFilterOptions } from '@/types/contract';
import { Input, Paginator, Select, Typography } from '@/ui';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import type { OptionType } from '@/ui/components/Form/Select/types';
import NotAuthorized from '@/ui/components/NotAuthorized';
import { resolveGridTheme } from '@/utils/gridTheme';
import snakeToTitleCase from '@/utils/snakeToTitleCase';
import { CONTRACT_VIEW, hasPermission } from '@/utils/userPermission';

import {
  ContractDetailCell,
  type ContractGridRow,
  createColumnDefs,
  DEFAULT_ORDERING,
  isDetailRow,
  SORTABLE_FIELDS
} from './columns';
import './styles.css';

ModuleRegistry.registerModules([AllCommunityModule]);

// Mirrors the params of backend/homebase/contracts/filters/contract_filter.py.
const FILTER_PARAMS = [
  'search',
  'name',
  'status',
  'jurisdiction',
  'program_family',
  'phase_type',
  'covers'
];

const DEFAULT_COL_DEF: ColDef = { sortable: true, resizable: false };

const getRowId = (params: GetRowIdParams<ContractGridRow>) => params.data.id;

const isFullWidthRow = (params: IsFullWidthRowParams<ContractGridRow>) =>
  isDetailRow(params.rowNode.data);

const getRowHeight = (params: RowHeightParams<ContractGridRow>) =>
  isDetailRow(params.data) ? 180 : undefined;

// The grid also sorts the fetched page locally; keep each detail row under its contract.
const postSortRows = (params: PostSortRowsParams<ContractGridRow>) => {
  const details = new Map(
    params.nodes.flatMap((node) =>
      isDetailRow(node.data) ? [[node.data.detailOf.id, node] as const] : []
    )
  );
  const ordered = params.nodes
    .filter((node) => !isDetailRow(node.data))
    .flatMap((node) => {
      const detail = node.data ? details.get(node.data.id) : undefined;
      return detail ? [node, detail] : [node];
    });
  params.nodes.splice(0, params.nodes.length, ...ordered);
};

/**
 * Each row is the current version of a contract, expandable into the insurance entities it
 * covers. Filtering, sorting and paging run server-side and live in the URL.
 */
const ContractsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { resolvedTheme, darkStyle } = useContext(AppContext);
  const gridRef = useRef<AgGridReact<ContractGridRow>>(null);
  const isCompact = useIsSmallScreen(1536);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const gridTheme = useMemo(
    () =>
      resolveGridTheme(resolvedTheme, darkStyle).withParams({
        // Set on the theme, not the rowHeight prop, so cell text centers in the row.
        rowHeight: 52,
        columnBorder: true,
        headerColumnBorder: true,
        // The Card draws the outer border, so the grid's own rounded one would double it.
        wrapperBorder: false,
        wrapperBorderRadius: 0
      }),
    [resolvedTheme, darkStyle]
  );

  const page = Number(searchParams.get('page') ?? '1') || 1;
  const ordering = searchParams.get('ordering') ?? DEFAULT_ORDERING;

  const requestParams = useMemo(() => {
    const params: Record<string, string> = { page: String(page), ordering };
    FILTER_PARAMS.forEach((key) => {
      const value = searchParams.get(key);
      if (value) params[key] = value;
    });
    return params;
  }, [searchParams, page, ordering]);

  const { data, isPending } = useDugoutContracts(requestParams);
  const { data: programFamilies } = useDugoutActiveProgramFamilies();
  const filterOptions = useMemo<ContractFilterOptions | undefined>(
    () =>
      programFamilies
        ? {
            statuses: CONTRACT_STATUSES,
            drug_categories: DRUG_CATEGORIES,
            program_families: programFamilies
          }
        : undefined,
    [programFamilies]
  );
  const { data: jurisdictions } = useJurisdictions();
  const { data: phaseTypes } = usePhaseTypes();
  const contracts = useMemo(() => data?.data?.results ?? [], [data]);
  const totalCount = data?.data?.count ?? 0;
  const totalPages = data?.data?.total_pages ?? 0;

  const setParam = useCallback(
    (key: string, value: string) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        next.set('page', '1');
        return next;
      }),
    [setSearchParams]
  );

  const pickerProps = (key: string, label: string, options: OptionType<string>[]) => {
    const values = (searchParams.get(key) ?? '').split(',').filter(Boolean);
    return {
      label,
      options,
      selectedOptions: options.filter((option) => values.includes(option.value)),
      onChange: (selected: OptionType<string>[]) =>
        setParam(key, selected.map((option) => option.value).join(','))
    };
  };

  const searchText = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(searchText);
  useEffect(() => {
    if (searchInput === searchText) return;
    const timeout = setTimeout(() => setParam('search', searchInput), 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);
  useEffect(() => setSearchInput(searchText), [searchText]);

  const pickers = useMemo(
    () => [
      {
        key: 'jurisdiction',
        label: 'Jurisdiction',
        placeholder: 'All jurisdictions',
        options: (jurisdictions ?? []).map((jurisdiction) => ({
          value: jurisdiction.id,
          label: jurisdiction.name
        }))
      },
      {
        key: 'program_family',
        label: 'Program family',
        placeholder: 'All program families',
        options: (filterOptions?.program_families ?? []).map((family) => ({
          value: family.id,
          label: family.name
        }))
      },
      {
        key: 'phase_type',
        label: 'Phase type',
        placeholder: 'All phase types',
        // Phase types repeat per program; the filter matches on their name.
        options: [...new Set((phaseTypes?.data ?? []).map((phaseType) => phaseType.name))].map(
          (name) => ({ value: name, label: snakeToTitleCase(name) })
        )
      }
    ],
    [filterOptions, jurisdictions, phaseTypes]
  );

  const rowData = useMemo<ContractGridRow[]>(
    () =>
      contracts.flatMap((contract) =>
        expandedIds.has(contract.id)
          ? [contract, { id: `${contract.id}-detail`, detailOf: contract }]
          : [contract]
      ),
    [contracts, expandedIds]
  );

  const toggleExpanded = useCallback(
    (id: string) =>
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    []
  );

  const gridContext = useMemo<ContractsGridContextInterface>(
    () => ({
      expandedIds,
      filterOptions,
      getParam: (key) => searchParams.get(key) ?? '',
      setParam,
      toggleExpanded
    }),
    [expandedIds, filterOptions, searchParams, setParam, toggleExpanded]
  );

  const applySortToGrid = (api: GridReadyEvent<ContractGridRow>['api']) => {
    const colId = ordering.replace(/^-/, '');
    api.applyColumnState({
      state: SORTABLE_FIELDS.has(colId)
        ? [{ colId, sort: ordering.startsWith('-') ? 'desc' : 'asc' }]
        : [],
      defaultState: { sort: null }
    });
  };

  useEffect(() => {
    const api = gridRef.current?.api;
    if (api) applySortToGrid(api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordering]);

  const onSortChanged = () => {
    const columnState: ColumnState[] = gridRef.current?.api.getColumnState() ?? [];
    const sorted = columnState.find((column) => column.sort);
    const next = sorted ? `${sorted.sort === 'desc' ? '-' : ''}${sorted.colId}` : DEFAULT_ORDERING;
    if (next !== ordering) setParam('ordering', next);
  };

  const openContractRow = (event: RowClickedEvent<ContractGridRow>) => {
    if (!event.data || isDetailRow(event.data)) return;
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest?.('button, a, [role="button"], span[tabindex="0"]')) return;
    navigate(`${CONTRACTS_PATH}/${event.data.id}`);
  };

  const goToPage = (nextPage: number) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(nextPage));
      return next;
    });

  const columnDefs = useMemo(
    () => createColumnDefs(filterOptions?.drug_categories ?? []),
    [filterOptions]
  );

  return (
    <ContractsGridContext.Provider value={gridContext}>
      <Container type="full-width-page">
        <div className="mb-4">
          <PageHeader headingText="Insurance contracts" />
          <Typography muted>
            Each row is the current version of a contract. Expand a row to see the insurance
            entities it covers and the programs each one supports.
          </Typography>
        </div>

        <div className="mb-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-uniform-gray-700 dark:bg-uniform-gray-900">
          <div className="flex flex-wrap items-start gap-3">
            <Input
              className="min-w-[180px] flex-1"
              // Matches the multi Selects' height (py-1.5 + py-0.5 around a text-sm line).
              inputClassName="py-2"
              label="Search"
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search contract or provider…"
              value={searchInput}
            />
            {isCompact && (
              <>
                <Input
                  className="min-w-[180px] flex-1"
                  label="Contract name"
                  placeholder="Filter by name…"
                  value={searchParams.get('name') ?? ''}
                  onChange={(event) => setParam('name', event.target.value)}
                />
                <Input
                  className="min-w-[180px] flex-1"
                  label="Covers date"
                  type="date"
                  value={searchParams.get('covers') ?? ''}
                  onChange={(event) => setParam('covers', event.target.value)}
                />
                <div className="relative min-w-[180px] flex-1">
                  <Select
                    multiple
                    debounce={false}
                    placeholder="All statuses"
                    {...pickerProps(
                      'status',
                      'Status',
                      (filterOptions?.statuses ?? []).map(({ value, label }) => ({ value, label }))
                    )}
                  />
                </div>
              </>
            )}
            {pickers.map(({ key, label, placeholder, options }) => (
              <div key={key} className="relative min-w-[180px] flex-1">
                <Select
                  multiple
                  debounce={false}
                  placeholder={placeholder}
                  {...pickerProps(key, label, options)}
                />
              </div>
            ))}
          </div>
        </div>

        <Card className="contracts-grid overflow-hidden p-0" paddingClass="p-0">
          {isCompact ? (
            isPending ? (
              <div className="p-4">Loading…</div>
            ) : (
              <CompactContractsList contracts={contracts} />
            )
          ) : (
            <AgGridReact<ContractGridRow>
              ref={gridRef}
              theme={gridTheme}
              columnDefs={columnDefs}
              defaultColDef={DEFAULT_COL_DEF}
              domLayout="autoHeight"
              floatingFiltersHeight={38}
              getRowId={getRowId}
              isFullWidthRow={isFullWidthRow}
              fullWidthCellRenderer={ContractDetailCell}
              getRowHeight={getRowHeight}
              loading={isPending}
              onGridReady={(event) => applySortToGrid(event.api)}
              onSortChanged={onSortChanged}
              onRowClicked={openContractRow}
              getRowStyle={({ data }) =>
                data && !isDetailRow(data) ? { cursor: 'pointer' } : undefined
              }
              pagination={false}
              suppressCellFocus
              postSortRows={postSortRows}
              rowData={rowData}
            />
          )}
          <div className="flex justify-between gap-4 border-t border-uniform-gray-200 px-4 py-2 text-xs text-uniform-gray-500 dark:border-uniform-gray-700">
            <span>
              {isPending ? 'Loading…' : `${totalCount} contract${totalCount === 1 ? '' : 's'}`}
            </span>
            <span>
              All = every insurance entity has every active program in the category · Some = some
              insurance entities and/or programs · None = not contracted. Hover a cell for
              per-family counts.
            </span>
          </div>
        </Card>

        <div className="mt-4">
          <Paginator currentPage={page} totalPages={totalPages} onPageChange={goToPage} />
        </div>
      </Container>
    </ContractsGridContext.Provider>
  );
};

const Contracts = () => {
  const { permissions } = useContext(AppContext);
  if (!hasPermission({ permissions, requiredPermission: CONTRACT_VIEW })) {
    return <NotAuthorized />;
  }
  return <ContractsList />;
};

export default Contracts;
